import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base styles with game-like feel
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        // Primary - Bioluminescent cyan glow
        default:
          'bg-gradient-to-b from-primary to-primary/80 text-primary-foreground shadow-[0_0_15px_rgba(34,211,238,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(0,0,0,0.2)] hover:shadow-[0_0_25px_rgba(34,211,238,0.5),inset_0_1px_0_rgba(255,255,255,0.2)] hover:from-primary/90 hover:to-primary/70 border border-primary/50',
        // Destructive - Deep crimson
        destructive:
          'bg-gradient-to-b from-destructive to-destructive/80 text-destructive-foreground shadow-[0_0_15px_rgba(220,38,38,0.2),inset_0_1px_0_rgba(255,255,255,0.1),inset_0_-1px_0_rgba(0,0,0,0.2)] hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] hover:from-destructive/90 border border-red-500/30',
        // Outline - Luminescent border
        outline:
          'border-2 border-biolume-cyan/50 bg-transparent text-biolume-cyan shadow-[0_0_10px_rgba(34,211,238,0.1)] hover:bg-biolume-cyan/10 hover:shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:border-biolume-cyan/70',
        // Secondary - Muted panel style
        secondary:
          'bg-gradient-to-b from-secondary to-secondary/80 text-secondary-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.05),inset_0_-1px_0_rgba(0,0,0,0.1)] hover:from-secondary/80 hover:to-secondary/60 border border-border/50',
        // Ghost - Subtle hover
        ghost:
          'text-foreground/80 hover:bg-secondary/50 hover:text-foreground hover:shadow-[0_0_10px_rgba(34,211,238,0.1)]',
        // Link - Glowing underline
        link: 'text-biolume-cyan underline-offset-4 hover:underline hover:text-biolume-glow',
        // Gold - Treasure/accent button
        gold: 'bg-gradient-to-b from-treasure-gold to-treasure-bronze text-abyss-void shadow-[0_0_15px_rgba(212,165,116,0.3),inset_0_1px_0_rgba(255,255,255,0.3),inset_0_-1px_0_rgba(0,0,0,0.2)] hover:shadow-[0_0_25px_rgba(212,165,116,0.5)] hover:from-treasure-gold/90 border border-treasure-gold/50 font-bold',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-md px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, ...properties },
  reference
) {
  return (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      ref={reference}
      {...properties}
    />
  );
});

export { Button, buttonVariants };
